import React, { useState } from 'react';
import { Plus, Edit, Trash2, X, Send, Eye, Mail } from 'lucide-react';

import axios, { API } from './apiClient';

// Auto-attach Bearer token for cross-origin auth
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const InvoicesTab = ({ invoices, invoiceSummary, customers, salesReps, onSave }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '', sales_rep_id: '', job_name: '', job_description: '',
    building_details: {
      building_type: '', square_footage: '', number_of_stories: '', number_of_bedrooms: '',
      number_of_bathrooms: '', garage_size: '', roof_type: '', foundation_type: '',
      exterior_finish: '', special_features: '', blueprints_included: false,
      engineering_included: false, delivery_included: false, installation_included: false, notes: ''
    },
    line_items: [{ _key: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0, total: 0 }],
    tax_rate: 0, discount_amount: 0, discount_description: '', payment_terms: 'Net 30',
    due_date: '', notes: '', internal_notes: ''
  });

  const resetForm = () => setFormData({
    customer_id: '', sales_rep_id: '', job_name: '', job_description: '',
    building_details: {
      building_type: '', square_footage: '', number_of_stories: '', number_of_bedrooms: '',
      number_of_bathrooms: '', garage_size: '', roof_type: '', foundation_type: '',
      exterior_finish: '', special_features: '', blueprints_included: false,
      engineering_included: false, delivery_included: false, installation_included: false, notes: ''
    },
    line_items: [{ _key: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0, total: 0 }],
    tax_rate: 0, discount_amount: 0, discount_description: '', payment_terms: 'Net 30',
    due_date: '', notes: '', internal_notes: ''
  });

  const addLineItem = () => {
    setFormData({ ...formData, line_items: [...formData.line_items, { _key: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0, total: 0 }] });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData({ ...formData, line_items: formData.line_items.filter((_, i) => i !== index) });
    }
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      items[index].total = (items[index].quantity || 0) * (items[index].unit_price || 0);
    }
    setFormData({ ...formData, line_items: items });
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxAmount = subtotal * (formData.tax_rate / 100);
    const total = subtotal + taxAmount - (formData.discount_amount || 0);
    return { subtotal, taxAmount, total };
  };

  const handleSave = async () => {
    const totals = calculateTotals();
    const payload = { ...formData, subtotal: totals.subtotal, total: totals.total };
    try {
      if (editingInvoice) {
        await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, payload, {});
      } else {
        await axios.post(`${API}/admin/invoices`, payload, {});
      }
      setShowModal(false);
      resetForm();
      setEditingInvoice(null);
      onSave();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); alert('Error saving invoice'); }
  };

  const handleStatusChange = async (invoiceId, status) => {
    try {
      await axios.put(`${API}/admin/invoices/${invoiceId}/status?status=${status}`, {}, {});
      onSave();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    await axios.delete(`${API}/admin/invoices/${invoiceId}`, {});
    onSave();
  };

  const handleSendEmail = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/admin/invoices/${invoiceId}/send`, {}, {});
      alert(res.data.message);
      onSave();
    } catch (e) { alert('Error sending invoice'); }
  };

  const getStatusColor = (status) => {
    const colors = { draft: 'bg-slate-100 text-slate-700', sent: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', overdue: 'bg-red-100 text-red-700', cancelled: 'bg-slate-200 text-slate-500' };
    return colors[status] || 'bg-slate-100';
  };

  const totals = calculateTotals();

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Invoices</h2>
      
      {/* Summary Cards */}
      {invoiceSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border rounded-lg p-4"><p className="text-sm text-slate-500">Total Invoices</p><p className="text-2xl font-bold">{invoiceSummary.total_invoices}</p></div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4"><p className="text-sm text-green-600">Paid Revenue</p><p className="text-2xl font-bold text-green-700">${invoiceSummary.total_revenue?.toLocaleString() || '0'}</p></div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4"><p className="text-sm text-blue-600">Pending</p><p className="text-2xl font-bold text-blue-700">${invoiceSummary.total_pending?.toLocaleString() || '0'}</p></div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-sm text-red-600">Overdue</p><p className="text-2xl font-bold text-red-700">${invoiceSummary.total_overdue?.toLocaleString() || '0'}</p></div>
        </div>
      )}

      {/* Create Invoice Button */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={() => { setEditingInvoice(null); resetForm(); setShowModal(true); }} className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded flex items-center" data-testid="create-invoice-btn">
          <Plus size={18} className="mr-2" />Create Invoice
        </button>
      </div>

      {/* Invoices List */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left py-3 px-4">Invoice #</th>
                <th className="text-left py-3 px-4">Date</th>
                <th className="text-left py-3 px-4">Customer</th>
                <th className="text-left py-3 px-4">Building Type</th>
                <th className="text-left py-3 px-4">Sales Rep</th>
                <th className="text-right py-3 px-4">Total</th>
                <th className="text-center py-3 px-4">Status</th>
                <th className="text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b hover:bg-slate-50">
                  <td className="py-3 px-4 font-medium">{inv.invoice_number}</td>
                  <td className="py-3 px-4">{inv.invoice_date}</td>
                  <td className="py-3 px-4">
                    <div>{inv.customer_name || inv.customer_company}</div>
                    <div className="text-xs text-slate-500">{inv.customer_email}</div>
                  </td>
                  <td className="py-3 px-4 capitalize">{inv.building_details?.building_type || '-'}</td>
                  <td className="py-3 px-4">{inv.sales_rep_name || '-'}</td>
                  <td className="py-3 px-4 text-right font-bold">${inv.total?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-center">
                    <select value={inv.status} onChange={(e) => handleStatusChange(inv.id, e.target.value)} className={`px-2 py-1 rounded text-xs ${getStatusColor(inv.status)}`}>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center space-x-2">
                      <button onClick={() => setViewingInvoice(inv)} className="text-blue-500 hover:text-blue-700" title="View"><Eye size={16} /></button>
                      <button onClick={() => { setEditingInvoice(inv); setFormData({
                        customer_id: inv.customer_id || '', sales_rep_id: inv.sales_rep_id || '',
                        job_name: inv.job_name || '', job_description: inv.job_description || '',
                        building_details: inv.building_details || {},
                        line_items: inv.line_items?.length ? inv.line_items : [{ _key: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0, total: 0 }],
                        tax_rate: inv.tax_rate || 0, discount_amount: inv.discount_amount || 0,
                        discount_description: inv.discount_description || '', payment_terms: inv.payment_terms || 'Net 30',
                        due_date: inv.due_date || '', notes: inv.notes || '', internal_notes: inv.internal_notes || ''
                      }); setShowModal(true); }} className="text-slate-500 hover:text-slate-700" title="Edit"><Edit size={16} /></button>
                      <button onClick={() => handleSendEmail(inv.id)} className="text-green-500 hover:text-green-700" title="Send Email"><Mail size={16} /></button>
                      <button onClick={() => handleDelete(inv.id)} className="text-red-500 hover:text-red-700" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invoices.length === 0 && <p className="text-center text-slate-500 py-8">No invoices yet. Create your first invoice!</p>}
        </div>
      </div>

      {/* Create/Edit Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-slate-800 text-white p-6">
              <h3 className="text-xl font-bold">{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</h3>
              <p className="text-slate-300 text-sm">3D METAL HOME BUILDING | 13355 Noel Rd Suite: 1100, Dallas, TX 75240 | 972-972-8989</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer & Sales Rep */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 border-b pb-2">Customer Information</h4>
                  <select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} className="w-full px-3 py-2 border rounded mb-2">
                    <option value="">Select Customer...</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.company_name} - {c.first_name} {c.last_name}</option>)}
                  </select>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-700 mb-3 border-b pb-2">Sales Representative</h4>
                  <select value={formData.sales_rep_id} onChange={(e) => setFormData({ ...formData, sales_rep_id: e.target.value })} className="w-full px-3 py-2 border rounded">
                    <option value="">Select Sales Rep...</option>
                    {salesReps.map(r => <option key={r.id} value={r.id}>{r.first_name} {r.last_name}</option>)}
                  </select>
                </div>
              </div>

              {/* Building Details */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3 border-b pb-2">Building Details</h4>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Building Type *</label>
                    <select value={formData.building_details.building_type} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, building_type: e.target.value } })} className="w-full px-3 py-2 border rounded">
                      <option value="">Select Type...</option>
                      <option value="home">Home</option>
                      <option value="office">Office</option>
                      <option value="warehouse">Warehouse</option>
                      <option value="commercial">Commercial</option>
                      <option value="barndominium">Barndominium</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium mb-1">Square Footage</label><input type="number" value={formData.building_details.square_footage || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, square_footage: e.target.value } })} className="w-full px-3 py-2 border rounded" /></div>
                  <div><label className="block text-sm font-medium mb-1"># of Stories</label><input type="number" value={formData.building_details.number_of_stories || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, number_of_stories: e.target.value } })} className="w-full px-3 py-2 border rounded" /></div>
                  <div><label className="block text-sm font-medium mb-1"># of Bedrooms</label><input type="number" value={formData.building_details.number_of_bedrooms || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, number_of_bedrooms: e.target.value } })} className="w-full px-3 py-2 border rounded" /></div>
                  <div><label className="block text-sm font-medium mb-1"># of Bathrooms</label><input type="number" step="0.5" value={formData.building_details.number_of_bathrooms || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, number_of_bathrooms: e.target.value } })} className="w-full px-3 py-2 border rounded" /></div>
                  <div><label className="block text-sm font-medium mb-1">Garage Size</label><input type="text" value={formData.building_details.garage_size || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, garage_size: e.target.value } })} className="w-full px-3 py-2 border rounded" placeholder="2-car, 3-car..." /></div>
                  <div><label className="block text-sm font-medium mb-1">Roof Type</label><input type="text" value={formData.building_details.roof_type || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, roof_type: e.target.value } })} className="w-full px-3 py-2 border rounded" placeholder="Metal, Shingle..." /></div>
                  <div><label className="block text-sm font-medium mb-1">Foundation</label><input type="text" value={formData.building_details.foundation_type || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, foundation_type: e.target.value } })} className="w-full px-3 py-2 border rounded" placeholder="Slab, Pier..." /></div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div><label className="block text-sm font-medium mb-1">Exterior Finish</label><input type="text" value={formData.building_details.exterior_finish || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, exterior_finish: e.target.value } })} className="w-full px-3 py-2 border rounded" /></div>
                  <div><label className="block text-sm font-medium mb-1">Special Features</label><input type="text" value={formData.building_details.special_features || ''} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, special_features: e.target.value } })} className="w-full px-3 py-2 border rounded" /></div>
                </div>
                <div className="flex flex-wrap gap-4 mt-4">
                  <label className="flex items-center"><input type="checkbox" checked={formData.building_details.blueprints_included} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, blueprints_included: e.target.checked } })} className="mr-2" />Blueprints Included</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.building_details.engineering_included} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, engineering_included: e.target.checked } })} className="mr-2" />Engineering Included</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.building_details.delivery_included} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, delivery_included: e.target.checked } })} className="mr-2" />Delivery Included</label>
                  <label className="flex items-center"><input type="checkbox" checked={formData.building_details.installation_included} onChange={(e) => setFormData({ ...formData, building_details: { ...formData.building_details, installation_included: e.target.checked } })} className="mr-2" />Installation Included</label>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex justify-between items-center mb-3 border-b pb-2">
                  <h4 className="font-semibold text-slate-700">Line Items</h4>
                  <button onClick={addLineItem} className="text-blue-500 hover:text-blue-700 flex items-center text-sm"><Plus size={16} className="mr-1" />Add Item</button>
                </div>
                <div className="space-y-2">
                  {formData.line_items.map((item, idx) => (
                    <div key={item._key || idx} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded">
                      <div className="col-span-5"><input type="text" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" className="w-full px-2 py-1 border rounded text-sm" /></div>
                      <div className="col-span-2"><input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)} placeholder="Qty" className="w-full px-2 py-1 border rounded text-sm" /></div>
                      <div className="col-span-2"><input type="number" value={item.unit_price} onChange={(e) => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} placeholder="Unit Price" className="w-full px-2 py-1 border rounded text-sm" /></div>
                      <div className="col-span-2 text-right font-medium">${(item.quantity * item.unit_price).toLocaleString()}</div>
                      <div className="col-span-1 text-center"><button onClick={() => removeLineItem(idx)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">${totals.subtotal.toLocaleString()}</span></div>
                  <div className="flex justify-between items-center">
                    <span>Tax (%):</span>
                    <input type="number" value={formData.tax_rate} onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })} className="w-20 px-2 py-1 border rounded text-sm text-right" />
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Discount:</span>
                    <input type="number" value={formData.discount_amount} onChange={(e) => setFormData({ ...formData, discount_amount: parseFloat(e.target.value) || 0 })} className="w-20 px-2 py-1 border rounded text-sm text-right" />
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span className="text-green-600">${totals.total.toLocaleString()}</span></div>
                </div>
              </div>

              {/* Payment Terms & Notes */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Terms</label>
                  <select value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} className="w-full px-3 py-2 border rounded">
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 60">Net 60</option>
                    <option value="50% Deposit">50% Deposit Required</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-1">Due Date</label><input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Notes (visible to customer)</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="2" className="w-full px-3 py-2 border rounded" placeholder="Thank you for your business!"></textarea></div>
              <div><label className="block text-sm font-medium mb-1">Internal Notes (not visible)</label><textarea value={formData.internal_notes} onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })} rows="2" className="w-full px-3 py-2 border rounded bg-yellow-50"></textarea></div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t bg-slate-50">
              <button onClick={() => { setShowModal(false); setEditingInvoice(null); resetForm(); }} className="px-6 py-2 border rounded">Cancel</button>
              <button onClick={handleSave} className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600">{editingInvoice ? 'Update Invoice' : 'Create Invoice'}</button>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewingInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Invoice Header */}
            <div className="bg-slate-800 text-white p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold">3D METAL HOME BUILDING</h2>
                  <p className="text-slate-300">13355 Noel Rd Suite: 1100, Dallas, TX 75240</p>
                  <p className="text-slate-300">Phone: 972-972-8989 | Email: Sales@3dmetalhomebuilding.com</p>
                </div>
                <div className="text-right">
                  <h3 className="text-xl font-bold">INVOICE</h3>
                  <p className="text-slate-300">#{viewingInvoice.invoice_number}</p>
                  <p className="text-slate-300">Date: {viewingInvoice.invoice_date}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Customer & Project Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-50 p-4 rounded">
                  <h4 className="font-semibold mb-2">Bill To:</h4>
                  <p className="font-medium">{viewingInvoice.customer_name}</p>
                  <p>{viewingInvoice.customer_company}</p>
                  <p>{viewingInvoice.customer_address}</p>
                  <p>{viewingInvoice.customer_city} {viewingInvoice.customer_state} {viewingInvoice.customer_zip}</p>
                  <p>{viewingInvoice.customer_email}</p>
                  <p>{viewingInvoice.customer_phone}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded">
                  <h4 className="font-semibold mb-2">Project Details:</h4>
                  <p><span className="text-slate-500">Building Type:</span> <span className="capitalize font-medium">{viewingInvoice.building_details?.building_type || 'N/A'}</span></p>
                  <p><span className="text-slate-500">Square Footage:</span> {viewingInvoice.building_details?.square_footage || 'N/A'}</p>
                  <p><span className="text-slate-500">Stories:</span> {viewingInvoice.building_details?.number_of_stories || 'N/A'}</p>
                  <p><span className="text-slate-500">Sales Rep:</span> {viewingInvoice.sales_rep_name || 'N/A'}</p>
                  <p><span className="text-slate-500">Status:</span> <span className={`px-2 py-0.5 rounded text-xs uppercase ${getStatusColor(viewingInvoice.status)}`}>{viewingInvoice.status}</span></p>
                </div>
              </div>

              {/* Line Items */}
              <table className="w-full mb-6">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left py-2 px-3">Description</th>
                    <th className="text-center py-2 px-3">Qty</th>
                    <th className="text-right py-2 px-3">Unit Price</th>
                    <th className="text-right py-2 px-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewingInvoice.line_items || []).map((item, idx) => (
                    <tr key={item.description + "-" + idx} className="border-b">
                      <td className="py-2 px-3">{item.description}</td>
                      <td className="py-2 px-3 text-center">{item.quantity}</td>
                      <td className="py-2 px-3 text-right">${item.unit_price?.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right">${item.total?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end mb-6">
                <div className="w-64">
                  <div className="flex justify-between py-1"><span>Subtotal:</span><span>${viewingInvoice.subtotal?.toLocaleString()}</span></div>
                  <div className="flex justify-between py-1"><span>Tax ({viewingInvoice.tax_rate}%):</span><span>${viewingInvoice.tax_amount?.toLocaleString()}</span></div>
                  {viewingInvoice.discount_amount > 0 && <div className="flex justify-between py-1"><span>Discount:</span><span>-${viewingInvoice.discount_amount?.toLocaleString()}</span></div>}
                  <div className="flex justify-between py-2 text-xl font-bold border-t mt-2"><span>Total:</span><span className="text-green-600">${viewingInvoice.total?.toLocaleString()}</span></div>
                </div>
              </div>

              {/* Notes */}
              {viewingInvoice.notes && (
                <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                  <p className="font-semibold">Payment Terms: {viewingInvoice.payment_terms}</p>
                  <p className="mt-2">{viewingInvoice.notes}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-6 border-t bg-slate-50">
              <div className="text-sm text-slate-500">Thank you for choosing 3D Metal Home Building!</div>
              <div className="flex space-x-3">
                <button onClick={() => handleSendEmail(viewingInvoice.id)} className="px-4 py-2 bg-blue-500 text-white rounded flex items-center"><Mail size={16} className="mr-2" />Send Email</button>
                <button onClick={() => setViewingInvoice(null)} className="px-4 py-2 border rounded">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Visitors Tab Component - Analytics Dashboard

export default InvoicesTab;
